import React from "react";
import { useSetState, useMountedState } from "react-use";
import { merge, tap } from "rxjs";
import dayjs from "dayjs";
import { map } from "rxjs/operators";

import { AsyncState } from "react-use/lib/useAsync";

import api from "../api";
import { WarrantQuery, WarrantQueryType } from "../api/newApi/getWarrantQuery";

import { BnsSubject } from "../websocket/quote";

export interface warrantrInquireParams {
  cuid: string;
  symid: string;
  cp: string;
  p1: number;
  p2: number;
  d1: number;
  d2: number;
  r1: number;
  r2: number;
  pub: string;
  ep1: number;
  ep2: number;
  qty1: number;
  qty2: number;
  df1: number;
  df2: number;
  lv1: number;
  lv2: number;
  page: number;
  tableAsc: string;
  asc: boolean;
  retryToggle: boolean;
  sessionId: string | null;
  mtype: string;
}

export interface BNSType {
  "11000": string;
  PriceDec: string;
  delta: string;
  diff_ratio: string;
  forluma_price: string;
  gamma: string;
  lever: string;
  percent_buy: string;
  percent_sale: string;
  publisher: string;
  rho: string;
  run_money: string;
  run_name: string;
  run_num: string;
  run_percent: string;
  stock_buy: string;
  stock_id: string;
  stock_money: string;
  stock_name: string;
  stock_percent: string;
  stock_sale: string;
  surplus_day: string;
  target_money: string;
  target_percent: string;
  theta: string;
  vega: string;
  volume: string;
}

export const useWarrantrInquire = function ({
  cuid,
  symid,
  cp,
  p1,
  p2,
  d1,
  d2,
  r1,
  r2,
  pub,
  ep1,
  ep2,
  qty1,
  qty2,
  df1,
  df2,
  lv1,
  lv2,
  page,
  tableAsc,
  asc,
  retryToggle,
  sessionId,
  mtype,
}: warrantrInquireParams): AsyncState<WarrantQuery> {
  const isMount = useMountedState();
  const [warrantState, setWarrantState] = useSetState<AsyncState<WarrantQuery>>(
    {
      loading: true,
    }
  );
  const { loading, error } = warrantState;
  const [symbols, setSymbols] = React.useState<string[]>([]);
  
  // console.log('123')
  
  React.useEffect(() => {
    (async function () {
      if (sessionId === null) {
        return;
      }
      await api.registerTick({
        symbols: symbols,
        types: ["Tick", "1BA", "BNS"],
        // types: ["BNS"],
        masterSessionId: sessionId,
      });
    })();
  }, [symbols, sessionId]);

  React.useEffect(() => {
    (async function getWarrant() {
      setWarrantState(() => ({ loading: true }));

      const warrantQuery = await api.getWarrantQuery({
        cu_id: cuid,
        sym: symid,
        cp: cp,
        p1: p1,
        p2: p2,
        d1: d1,
        d2: d2,
        r1: r1,
        r2: r2,
        pub: pub,
        ep1: ep1,
        ep2: ep2,
        qty1: qty1,
        qty2: qty2,
        df1: df1,
        df2: df2,
        lv1: lv1,
        lv2: lv2,
        page: page,
        order: tableAsc,
        asc: asc,
      });

      if (!isMount()) {
        return;
      }

      if (warrantQuery?.err !== "0000") {
        setWarrantState(() => ({
          loading: false,
          error: new Error(warrantQuery?.err),
        }));
      } else {
        const symbols = warrantQuery.warrant
          ? warrantQuery.warrant.map((i) => i.stock_id + ".TW" + mtype)
          : [];
        setSymbols(symbols);
        setWarrantState(() => ({ loading: false, value: warrantQuery }));
      }
    })();
    
  }, [isMount, setWarrantState, cuid, retryToggle, page, tableAsc, asc]);

  React.useEffect(() => {
    //TickSubject, BidAskSubject,
    const subscriber = merge(BnsSubject)
      .pipe(
        // bufferTime(1000 / 2),
        // filter((messages) => messages.length > 0),
        tap((message) => {
          // console.log("message", message);
        }),
        map((messages) => {
          function Formatter(data: any): WarrantQueryType {
            const originalWarrantArray = warrantState.value?.warrant?.filter(v=>v.stock_id===data.stock_id)||[]

            return {
              stock_id: data.stock_id,
              stock_name: data.stock_name,
              stock_money: data.stock_money,
              stock_buy: data.stock_buy,
              stock_sale: data.stock_sale,
              stock_percent: data.stock_percent,
              target_money: data.target_money,
              target_percent: data.target_percent,
              run_money: data.run_money,
              run_num: data.run_num,
              run_percent: originalWarrantArray[0].run_percent,
              surplus_day: data.surplus_day,
              lever: data.lever,
              volume: data.volume,
              percent_buy: data.percent_buy,
              diff_ratio: originalWarrantArray[0].diff_ratio,
              target_name_1: data.target_name_1,
              target_name_2: data.target_name_2,
              publisher: data.publisher,
              favor: data.favor || false,
              ref_price: data.ref_price,
            };
          }
          const newDate = Formatter(messages);
          return newDate;
        })
      )
      .subscribe((message) => {
        if (!isMount()) {
          return;
        }
        setWarrantState((prev) => {
          const QueryTime = dayjs().format("YYYY/MM/DD HH:mm:ss");
          const newLists = message;
          let existingLists = prev.value?.warrant || [];

          function upsert(
            array: WarrantQueryType[],
            element: WarrantQueryType
          ) {
            const i = array.findIndex(
              (_element: any) => _element.stock_id === element.stock_id
            );
            if (i !== -1) {
              array[i] = element;
            } else {
              array.push(element);
            }
          }

          upsert(existingLists, newLists);

          // console.log("update", existingLists);
          const newState = {
            QueryTime: QueryTime,
            err: prev.value?.err || "",
            warrant: existingLists,
            pages: prev.value?.pages || 0,
            recs: prev.value?.recs || 0,
          };

          return {
            value: newState,
          };
        });
      });

    return () => {
      subscriber.unsubscribe();
    };
  }, [symbols, loading, error, setWarrantState, isMount]);
  return warrantState;
};

export default useWarrantrInquire;
